import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { lightTheme } from './theme';
import { PageContainer, ChatShell, MessageScrollArea } from './styledComponents';
import { BlinkingCursorStyle, MessageRenderer, ErrorMessage } from './MessageComponents';
import { PromptInput } from './PromptInput';
import { WelcomeScreen } from './WelcomeScreen';
import { NEW_CONVERSATION_OPTION, useChatLogic } from './useChatLogic';
import { authenticatedFetch } from './AuthenticatedChatTransport';
import { ChatSettingsDialog } from './ChatSettingsDialog';
import { useCurrentProfile } from '../../ContextWrapper';

const formatRelativeTimestamp = (timestamp) => {
  if (!timestamp) {
    return 'No activity yet';
  }

  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return 'No activity yet';
  }

  const diffMs = Date.now() - parsed.getTime();
  const diffMinutes = Math.round(diffMs / 60000);

  if (diffMinutes < 1) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hr${diffHours === 1 ? '' : 's'} ago`;
  }

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: parsed.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
};

export const ChatInterface = ({
  customerId,
  viewType = 'client',
}) => {
  const profileContext = useCurrentProfile();
  const profile = profileContext?.profile;
  const hasAdminPrivileges = profile?.type === 'admin';
  const canUseAdminControls = hasAdminPrivileges;

  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams();

  const {
    messages,
    effectiveInput,
    onPromptChange,
    handleSubmit,
    handleKeyDown,
    isLoading,
    isStreaming,
    error,
    showWelcome,
    conversations,
    selectedConversationId,
    selectConversation,
    startNewConversation,
    isConversationsLoading,
    isConversationMessagesLoading,
    historyError,
    stop,
    setPromptValue,
    debugMode,
    setDebugMode,
  } = useChatLogic(customerId, viewType, canUseAdminControls);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [isPromptLoading, setIsPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState('');

  useEffect(() => {
    if (!canUseAdminControls) {
      setIsSettingsOpen(false);
    }
  }, [canUseAdminControls]);

  // Load conversation from URL parameter
  useEffect(() => {
    if (conversationId && conversationId !== selectedConversationId) {
      selectConversation(conversationId);
    }
  }, [conversationId, selectedConversationId, selectConversation]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current && !isConversationMessagesLoading) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isConversationMessagesLoading]);

  const handleConversationChange = (event) => {
    const { value } = event.target;
    
    // Build the correct path based on current location
    let basePath;
    
    if (location.pathname.includes('/client/')) {
      // Client chat: /client/:customer_id/chat or /client/:customer_id/chat/:conversationId
      // Extract the client ID and rebuild the path
      const clientIdMatch = location.pathname.match(/\/client\/([^/]+)/);
      if (clientIdMatch) {
        basePath = `/client/${clientIdMatch[1]}/chat`;
      } else {
        basePath = location.pathname;
      }
    } else {
      // Admin chat: /chat or /chat/:conversationId
      basePath = '/chat';
    }
    
    if (value === NEW_CONVERSATION_OPTION) {
      startNewConversation();
      // Navigate to base chat route for new conversation
      navigate(basePath);
      return;
    }
    selectConversation(value);
    // Navigate to specific conversation route - append conversation ID
    navigate(`${basePath}/${value}`);
  };

  const conversationSelectValue = selectedConversationId ?? NEW_CONVERSATION_OPTION;
  const activeConversation = conversations.find(
    (conversation) => conversation.id === selectedConversationId
  );

  useEffect(() => {
    if (!isSettingsOpen || !canUseAdminControls) {
      return undefined;
    }

    let isCancelled = false;
    const loadPrompt = async () => {
      setIsPromptLoading(true);
      setPromptError('');
      try {
        const params = new URLSearchParams();
        if (customerId) {
          params.append('customer_id', customerId);
        }
        if (selectedConversationId) {
          params.append('conversation_id', selectedConversationId);
        }
        const endpoint = params.toString()
          ? `/api/v2/chat/system_prompt/?${params.toString()}`
          : '/api/v2/chat/system_prompt/';
        const response = await authenticatedFetch(endpoint);
        if (!response.ok) {
          throw new Error(`Failed to load system prompt (${response.status})`);
        }
        const payload = await response.json();
        if (!isCancelled) {
          setSystemPrompt(payload?.system_prompt ?? '');
        }
      } catch (promptLoadError) {
        console.error('Failed to load system prompt', promptLoadError);
        if (!isCancelled) {
          setPromptError(
            promptLoadError?.message || 'Unable to load system prompt.'
          );
        }
      } finally {
        if (!isCancelled) {
          setIsPromptLoading(false);
        }
      }
    };

    loadPrompt();

    return () => {
      isCancelled = true;
    };
  }, [customerId, isSettingsOpen, selectedConversationId, canUseAdminControls]);

  return (
    <ThemeProvider theme={lightTheme}>
      <BlinkingCursorStyle />
      <PageContainer>
        {/* Left side: Conversation dropdown - positioned absolutely */}
        <Box
          sx={{
            position: 'absolute',
            left: 16,
            top: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: 240,
            maxWidth: 240,
            zIndex: 10,
          }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: 240,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
              },
            }}
          >
            <InputLabel id="chat-conversation-select-label">Conversations</InputLabel>
            <Select
              labelId="chat-conversation-select-label"
              id="chat-conversation-select"
              label="Conversations"
              value={conversationSelectValue}
              onChange={handleConversationChange}
            >
              <MenuItem value={NEW_CONVERSATION_OPTION}>
                <ListItemText
                  primary="Start new conversation"
                  secondary="Fresh chat"
                />
              </MenuItem>
              {conversations.map((conversation) => (
                <MenuItem key={conversation.id} value={conversation.id}>
                  <ListItemText
                    primary={conversation.title || 'New Conversation'}
                    secondary={formatRelativeTimestamp(conversation.last_message_at)}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isConversationsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
              <CircularProgress size={16} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          )}
          {!isConversationsLoading && activeConversation && (
            <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
              {activeConversation.message_count === 1
                ? '1 message'
                : `${activeConversation.message_count ?? 0} messages`}
            </Typography>
          )}
        </Box>

        {canUseAdminControls && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setIsSettingsOpen(true)}
              sx={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                textTransform: 'none',
                boxShadow: '0 2px 6px rgba(15, 23, 42, 0.12)',
              }}
            >
              Settings
            </Button>
          </Box>
        )}
        
        {/* Right side: Chat area */}
        <ChatShell>

          {showWelcome ? (
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
              }}
            >
              <WelcomeScreen
                inputValue={effectiveInput}
                onInputChange={onPromptChange}
                onSubmit={handleSubmit}
                onKeyDown={handleKeyDown}
                isLoading={isLoading || isConversationMessagesLoading}
                onStop={stop}
                onPresetSelect={setPromptValue}
              />
            </Box>
          ) : (
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <MessageScrollArea>
                {isConversationMessagesLoading && messages.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      paddingY: 6,
                    }}
                  >
                    <CircularProgress size={20} sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading conversation…
                    </Typography>
                  </Box>
                ) : (
                  messages.map((message, index) => (
                    <MessageRenderer
                      key={message.id ?? `${index}-${message.role}`}
                      message={message}
                      index={index}
                      isStreaming={isStreaming}
                      isLastMessage={index === messages.length - 1}
                    />
                  ))
                )}

                {historyError && <ErrorMessage error={historyError} />}
                {error && <ErrorMessage error={error} />}

                <div ref={messagesEndRef} />
              </MessageScrollArea>

              <Box sx={{ padding: 2, borderTop: '1px solid #E5E7EB' }}>
                <PromptInput
                  value={effectiveInput}
                  onChange={onPromptChange}
                  onSubmit={handleSubmit}
                  onKeyDown={handleKeyDown}
                  isLoading={isLoading}
                  disabled={isConversationMessagesLoading}
                  elevated
                  placeholder="Message Lucro Assistant..."
                  onStop={stop}
                  onPresetSelect={setPromptValue}
                />
              </Box>
            </Stack>
          )}
        </ChatShell>
        {canUseAdminControls && (
          <ChatSettingsDialog
            open={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
            debugMode={debugMode}
            onDebugModeChange={setDebugMode}
            systemPrompt={systemPrompt}
            loadingPrompt={isPromptLoading}
            promptError={promptError}
          />
        )}
      </PageContainer>
    </ThemeProvider>
  );
};

export default ChatInterface;
